import {
  V2Request,
  V2Response,
  V2Transport,
} from '@jetfabric/schemas/lib/protocol-sdk-api-v2/protocol';
import { inject, injectable } from 'inversify';
import { DI_CONSTANTS, DI_TYPES } from './di-types';
import { JetfabricError } from './errors';
import { ApiRequestData, Transport } from './transport';
import { NativeFunctionsProvider } from './util/iframe-natives';
import { getWindowObject as _getWindowObject } from './util/window-provider';

// based on https://stackoverflow.com/a/37562814
const urlEncodeRequestData = (requestData: ApiRequestData) => {
  return Object.entries(requestData).reduce((acc, [k, v]) => {
    return `${acc}${acc.length > 0 ? '&' : ''}${encodeURIComponent(k)}=${encodeURIComponent(
      JSON.stringify(v)
    )}`;
  }, '');
};

@injectable()
export class HTTPTransport implements V2Transport {
  private apiEndpointUrl: string;
  private apiEndpointUrlV2: string;
  private nativeFunctionsProvider: NativeFunctionsProvider;
  private getWindowObject: typeof _getWindowObject;

  constructor(
    @inject(DI_CONSTANTS.API_ENDPOINT_URL) apiEndpointUrl: string,
    @inject(DI_CONSTANTS.API_ENDPOINT_URL_V2) apiEndpointUrlV2: string,
    @inject(DI_TYPES.NativeFunctionsProvider) nativeFunctionsProvider: NativeFunctionsProvider,
    @inject(DI_TYPES.WindowProvider) getWindowObject: typeof _getWindowObject
  ) {
    this.apiEndpointUrl = apiEndpointUrl;
    this.apiEndpointUrlV2 = apiEndpointUrlV2;
    this.nativeFunctionsProvider = nativeFunctionsProvider;
    this.getWindowObject = getWindowObject;
  }

  private async doSubmit(endpoint: string, requestData: Object): Promise<Object> {
    try {
      const nativeFunctions = await this.nativeFunctionsProvider.getNativeFunctions();
      const nativeFetch = nativeFunctions.fetch!;
      const nativeRequest = nativeFunctions.Request!;
      const response = await nativeFetch(
        new nativeRequest(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          },
          body: urlEncodeRequestData(requestData),
        })
      );

      if (response.status < 200 || response.status > 299) {
        throw new JetfabricError(
          `HTTP error: received status code ${response.status} from endpoint ${endpoint}`
        );
      }

      return await response.json();
    } catch (err) {
      if (err instanceof JetfabricError) {
        throw err;
      }
      throw new JetfabricError(`HTTP error: ${err + ''}`);
    }
  }

  async submitV2(requestData: V2Request): Promise<V2Response> {
    return (await this.doSubmit(this.apiEndpointUrlV2, requestData)) as V2Response;
  }
}
